import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, RefreshCw } from "lucide-react";
import { API, FUEL_TYPES, formatCurrency } from "./constants";
import axios from "axios";

export default function TrendsChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrends();
  }, []);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/gaspy/trends?days=30`);
      const formatted = resp.data.map((d) => ({
        ...d,
        date: new Date(d.timestamp).toLocaleDateString("en-NZ", { day: "numeric", month: "short" }),
        time: new Date(d.timestamp).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" }),
      }));
      setData(formatted);
    } catch (e) {
      console.error("Error fetching trends:", e);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="sf-card" style={{ padding: "12px 16px", minWidth: 180 }}>
        <p className="text-xs text-[#666666] font-mono mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={p.dataKey || p.name || i} className="flex items-center gap-2 mb-1">
            <span className="sf-dot" style={{ backgroundColor: p.color, width: 8, height: 8 }} />
            <span className="text-xs font-['Manrope']">{p.name}</span>
            <span className="text-xs font-mono font-bold ml-auto">{formatCurrency(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="sf-card page-enter delay-300">
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="sf-subheading flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#E52020]" />FUEL PRICE TRENDS
          </h2>
          <button onClick={fetchTrends} className="sf-icon-btn" title="Refresh trends" data-testid="refresh-trends-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[#E52020]" />
            <span className="ml-3 text-[#666666] font-mono">Loading trends...</span>
          </div>
        ) : data.length <= 1 ? (
          <div className="text-center py-12 text-[#666666]">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-['Manrope']">Not enough data yet</p>
            <p className="text-sm mt-1 font-mono">Price trends will appear as data is collected over time</p>
          </div>
        ) : (
          <div data-testid="trends-chart" style={{ width: "100%", height: 280, minWidth: 200, minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(229, 32, 32,0.1)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#666666", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "rgba(229, 32, 32,0.15)" }} />
                <YAxis tick={{ fontSize: 11, fill: "#666666", fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "rgba(229, 32, 32,0.15)" }} domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toFixed(2)}`} width={55} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, fontFamily: "Manrope" }} />
                <Line type="monotone" dataKey="diesel" name="Diesel" stroke={FUEL_TYPES.diesel.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="unleaded_91" name="Unleaded 91" stroke={FUEL_TYPES.unleaded_91.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="unleaded_95" name="Unleaded 95" stroke={FUEL_TYPES.unleaded_95.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
